import type { Action } from '../action/action'
import type { ActionData, ActionOfState } from '../action/action.types'
import AddressState from '../states/addressState'
import type {
    ActionConstructor,
    CurrentState,
    FlowTypeBFSM,
    GetData,
    PublishType,
    StateAny,
} from './bookingflowstatemachine.types'
import type ISubject from './subject'
import type { Observer, ObserverId } from './subject'
import { RRObject } from '@raskrask-dk/raskrask-utils'
import { IRepository, RepositoryFake } from '../repository'
import MoveBookingState from '../states/moveBookingState'
import ErrorState from '../states/errorstate'
import { BookingService } from '../service/service'
import type { IBookingService } from '../service/service.types'
import { CancelBookingAdminState } from '../adminstates/cancelbookingAdminState/cancelBookingAdminState'

export default class BookingflowStateMachine implements ISubject {
    private _stack: StateAny[] = []
    private _historyStack: StateAny[] = []
    private _observers: Observer[] = []
    private _suggestiveChoices: GetData<StateAny> | 'EMPTY' = 'EMPTY'
    public readonly service: IBookingService
    public repo: IRepository
    private _flowType: FlowTypeBFSM = 'Booking'

    public get flowType(): BookingflowStateMachine['_flowType'] {
        return this._flowType
    }

    public uuid: string = Math.random().toString()

    constructor(
        service?: IBookingService,
        flow?: FlowTypeBFSM,
        initialMachineData?: GetData<StateAny>
    ) {
        this.service = service ?? new BookingService(new RepositoryFake())
        this.repo = this.service.repo
        this._suggestiveChoices = initialMachineData ?? 'EMPTY'
        this._flowType = flow ?? 'Booking'
        this.initializeFirstState()
    }

    private initializeFirstState(): void {
        // const defineInitialState = this._flowType === "MoveBooking" || this._flowType === "CancelBooking" ? new MoveBookingState(this) : new AddressState(this);
        const getInitialState = (): StateAny => {
            switch (this._flowType as FlowTypeBFSM) {
                case 'Booking':
                    return new AddressState(this)
                case 'MoveBooking':
                    return new MoveBookingState(this)
                case 'CancelBooking':
                    return new MoveBookingState(this)
                case 'AdminCancelBooking':
                    return new CancelBookingAdminState(this)
                default:
                    return new AddressState(this)
            }
        }
        const defineInitialState = getInitialState()
        this.addToStateStack(defineInitialState)
    }

    public get suggestiveChoices(): GetData<StateAny> | 'EMPTY' {
        return this._suggestiveChoices
    }

    public set addSuggestions(choices: GetData<StateAny>) {
        this._suggestiveChoices = choices
    }

    /**
     * Calling this function will tell whether or not the supplied Action currently is doable in the BFSM.
     * @param actionClass
     * @returns
     */
    public canDo<S extends StateAny, GAction extends Action<string, any>>(
        actionClass: ActionConstructor<ActionOfState<S>, S, ActionData<GAction>>
    ): boolean {
        const action = new actionClass(this.currentState() as S, undefined)
        if (!action.isDoable()) return false
        return true
    }

    /**
     * This is magical! 🧛🪄🔮🧙
     * Calling this will make the BookingflowStateMachine progress in the flow.
     * Actions must be of the current state.
     * Tip: getActions() returns a list of "allowed" actions at the time of calling.
     * AUTOMATICALLY RESETS ILLEGAL VALUES IF ACTION REQUIRES IT. EG. CHANGING PARTNER TYPE
     *
     * @template S - Current state which action should be called on
     * @template GAction - The action to be called
     * @param {ActionConstructor<ActionOfState<S>, ActionData<GAction>>} actionClass - action to be called
     * @param {ActionData<GAction>} data
     */
    public doAction<S extends StateAny, GAction extends Action<string, any>>(
        actionClass: ActionConstructor<
            ActionOfState<S>,
            S,
            ActionData<GAction>
        >,
        data: ActionData<GAction>
    ): boolean {
        const action = new actionClass(this.currentState() as S, data)

        // 1) Do Action 2) Update Stack 3) Notify
        const resultingState =
            action.type === 'ERROR_ACTION'
                ? new ErrorState(this)
                : this.executeAction(action)

        const changedState = this.addToStateStack(resultingState)
        this.notify(changedState ? 'State changed' : 'Actionlist updated')

        return true
    }

    /**
     * Adds state to the stack if it's new
     * Also adds it to the history Stack
     * @returns true if state changed
     */
    private addToStateStack(state: StateAny): boolean {
        const isNewState = state !== this.currentState()
        if (isNewState) {
            this._stack.push(state)
            this._historyStack.push(state)
        }
        return isNewState
    }

    /**
     * Runs action on current state
     * Also call state unset so properties stay in sync
     * @returns what state action landed on
     */
    private executeAction(action: Action<string, unknown>): StateAny {
        const currentState = this.currentState()
        currentState.unset(action)
        const resultingState = currentState.doAction(action) as StateAny
        return resultingState
    }

    /**
     * List of actions which is allowed for the particular moment.
     * The answer is decided by the current state.
     *
     * @returns {Action<string, data>[]} list of actions and their required data type
     */
    public getActions = (): Action<string, any>[] =>
        this.currentState().getActions()

    /**
     * Current state on the stateMachine
     *
     * @returns {State<any>} the current state
     */
    public currentState(): StateAny {
        return this._stack[this._stack.length - 1]
    }

    /**
     * Returns to previous state in the statemachine
     *
     */
    public goBack = (): void => {
        this._stack.pop()
        this.notify('State changed')
    }

    public setState = (state: any): void => {
        this._stack.push(state)
        this.notify('State changed')
    }

    /**
     * The stack of states which the machine has gone through.
     * Herein all the choices and data will implicitly lie.
     *
     * @returns {State<any>[]} stack of states
     */
    public get stack(): StateAny[] {
        return [...this._stack]
    }

    /**
     * The stack of all states which the BFSM has gone through.
     * Herein all the choices and data will implicitly lie.
     *
     * @returns {State<any>[]} stack of states
     */
    public get historyStack(): StateAny[] {
        return [...this._historyStack]
    }

    /**
     * This functions loop through the history stack and creates a data Object
     * If more than 1 instance of a given State is present in the history stack the latest added instance is going to 'overwrite' earlier instanziations
     * @returns all data from all states
     */

    public getData<State extends CurrentState>(): GetData<State> {
        const preSetData =
            this._suggestiveChoices === 'EMPTY' ? {} : this._suggestiveChoices

        return this.stack.reduce((data, state) => {
            return { ...data, ...RRObject.removeUndefined(state.getData()) }
        }, preSetData) as GetData<State>
        /*
     const initialMachineData = this._initialMachineData || {};

    const dataOverWrittenByHistory = this.historyStack.reduce((data, state) => {
      return {...data, ...RRObject.removeUndefined(state.getData())};
    }, initialMachineData) as GetData;

    const dataOverWrittenByStack = this.stack.reduce((data, state) => {
      return {...data, ...RRObject.removeUndefined(state.getData())};
    }, dataOverWrittenByHistory) as GetData;

    return dataOverWrittenByStack;
    */

        // TODO: Should be reenabled. Alexander is working on a fix.
        //        atm causes a lot of errors in frontend
        // let result: any = {};
        // this._historyStack.forEach((state) => {
        //   const data = RRObject.removeUndefined(state.getData());
        //   result = {...result, ...data};
        // });
        // return result;
    }

    /**
     * Here an observer can subscribe to changes that happens
     * in the BFSM
     *
     * @param observer - the observer which subscribes to the machine
     */
    public attach = (observer: Observer): void => {
        this._observers.push(observer)
    }

    /**
     * Here an observer can unsubscribe to changes that happens
     * in the BFSM
     *
     * @param observer - the observer which subscribes to the machine
     */
    public detach = (observerId: ObserverId): void => {
        this._observers = this._observers.filter(
            (observer) => observer.id !== observerId
        )
    }

    /**
     * This will notify any observer about the changes that happened
     *
     * @param data - the observer which subscribes to the machine
     */
    public notify = (data: PublishType): void => {
        this._observers.forEach((observer) => {
            observer.notify(data, this)
        })
    }

    public resetFlow = (flowType: FlowTypeBFSM = 'Booking'): void => {
        this._flowType = flowType
        this._stack = []
        this._historyStack = []
        this._suggestiveChoices = 'EMPTY'
        this.initializeFirstState()
        this.notify('State changed')
    }
}
